import React, { useState } from "react";
import { useNavigate } from "react-router";
import { Button } from "../../components/ui/button";
import { useNotification } from "../../contexts/NotificationContext";
import { usePricing } from "../../contexts/PricingContext";
import { Zone, flattenIssues, zoneSchema } from "../../lib/pricing";
import { saveZone } from "../pricing/repository";
import { FormSection, SwitchField, TextField } from "../components/pricing/fields";

interface Props {
  id?: string;
  zone?: Zone;
}

export const PRICING_PATH = "/admin-management/others/pricing";

export default function ZoneForm({ id, zone }: Props) {
  const { zones } = usePricing();
  const [name, setName] = useState(zone?.name ?? "");
  const [active, setActive] = useState(zone?.active ?? true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const { showNotification } = useNotification();
  const navigate = useNavigate();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const order = zone?.order ?? (zones.length ? Math.max(...zones.map((z) => z.order)) + 1 : 1);
    const parsed = zoneSchema.safeParse({ name, order, active });
    if (!parsed.success) {
      setErrors(flattenIssues(parsed.error));
      return;
    }
    setErrors({});
    setSaving(true);
    try {
      await saveZone(parsed.data, id);
      showNotification("success", id ? "Região atualizada." : "Região criada.");
      navigate(`${PRICING_PATH}?tab=zones`);
    } catch {
      showNotification("error", "Não foi possível salvar a região.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="space-y-6" onSubmit={handleSubmit}>
      <FormSection title="Região">
        <TextField
          id="zone-name"
          label="Nome *"
          placeholder="Ex.: Norte da Ilha + Aeroporto"
          value={name}
          maxLength={80}
          onChange={setName}
          error={errors.name}
          hint="Agrupa localidades com o mesmo preço."
        />
        <SwitchField
          label="Região ativa"
          description="Regiões inativas não aparecem para o cliente."
          checked={active}
          onChange={setActive}
        />
      </FormSection>
      <Button type="submit" className="w-full" disabled={saving}>
        {id ? "Atualizar" : "Adicionar"}
      </Button>
    </form>
  );
}
